import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {apiRetrieveImproved} from "../../../api";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/embeds')({
    component: RouteComponent,
})

type Copied = {user_uid: string; name: string; email: string; copied_at: string};
type Site = {host: string; views: number; events: number; first_seen: string; last_seen: string};
type EmbeddedEvent = {
    handle: string; kind: string; title: string | null; owner: string | null;
    owner_email: string | null; views: number; hosts: number; last_seen: string;
};
type Embeds = {
    stats: {organizers: number; copied: number; live_sites: number; live_events: number; total_views: number};
    copied: Copied[];
    sites: Site[];
    events: EmbeddedEvent[];
};

function Stat({label, value, sub}: {label: string; value: number; sub?: string}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 px-4 py-3">
            <div className="text-2xl font-bold tabular-nums">{value}</div>
            <div className="text-sm font-medium">{label}</div>
            {sub && <div className="text-xs text-default-400">{sub}</div>}
        </div>
    );
}

function RouteComponent() {
    const query = useQuery<Embeds>({
        queryKey: ['master-embeds'],
        queryFn:  () => apiRetrieveImproved('master/embeds' as any, {}),
    });

    const d = query.data;
    if (!d) {
        return <><Header /><div className="flex justify-center py-12"><Spinner size="lg" /></div></>;
    }

    const pct = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 100) + '%' : '—');

    return <>
        <Header />
        <div className="mx-auto w-[98%] max-w-6xl py-4">
            <h1 className="text-2xl font-bold">Embeds</h1>
            <p className="mb-1 text-sm text-default-500">
                Who has taken the "Embed on your website" code, and whose site is actually showing it.
            </p>
            <p className="mb-4 text-xs text-default-400">
                Installs are counted when an embed renders on a site that isn't ScorePal — the preview inside the
                embed dialog never counts. Only the host domain and a daily view count are stored, no visitor data.
                A site whose browser sends no referrer can't be attributed, so live numbers are a floor, not a ceiling.
            </p>

            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
                <Stat label="Organizers" value={d.stats.organizers} sub="real accounts" />
                <Stat label="Copied the code" value={d.stats.copied} sub={pct(d.stats.copied, d.stats.organizers) + ' of organizers'} />
                <Stat label="Sites live" value={d.stats.live_sites} sub="domains showing an embed" />
                <Stat label="Events embedded" value={d.stats.live_events} />
                <Stat label="Embed views" value={d.stats.total_views} />
            </div>

            <h2 className="mb-2 text-lg font-semibold">Sites showing an embed</h2>
            {d.sites.length === 0 ? (
                <div className="mb-6 rounded-xl border border-default-200 py-10 text-center text-sm text-default-500">
                    No embed has rendered on an external site yet.
                    {d.stats.copied > 0 && <> {d.stats.copied} {d.stats.copied === 1 ? 'organizer has' : 'organizers have'} copied the code — it looks like it never got pasted in.</>}
                </div>
            ) : (
                <div className="mb-6 overflow-x-auto rounded-xl border border-default-200">
                    <table className="w-full min-w-[640px] text-sm">
                        <thead>
                            <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                <th className="px-3 py-2">Website</th>
                                <th className="px-3 py-2">Events</th>
                                <th className="px-3 py-2">Views</th>
                                <th className="px-3 py-2">First seen</th>
                                <th className="px-3 py-2">Last seen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {d.sites.map((s) => (
                                <tr key={s.host} className="border-b border-default-100 last:border-0">
                                    <td className="px-3 py-2 font-medium">
                                        <a href={`https://${s.host}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{s.host}</a>
                                    </td>
                                    <td className="px-3 py-2 tabular-nums">{s.events}</td>
                                    <td className="px-3 py-2 tabular-nums">{s.views}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-default-500">{s.first_seen}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-default-500">{s.last_seen}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {d.events.length > 0 && (
                <>
                    <h2 className="mb-2 text-lg font-semibold">Events being embedded</h2>
                    <div className="mb-6 overflow-x-auto rounded-xl border border-default-200">
                        <table className="w-full min-w-[720px] text-sm">
                            <thead>
                                <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                    <th className="px-3 py-2">Event</th>
                                    <th className="px-3 py-2">Type</th>
                                    <th className="px-3 py-2">Owner</th>
                                    <th className="px-3 py-2">Sites</th>
                                    <th className="px-3 py-2">Views</th>
                                    <th className="px-3 py-2">Last seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.events.map((e) => (
                                    <tr key={e.handle} className="border-b border-default-100 last:border-0">
                                        <td className="px-3 py-2 font-medium">{e.title || <span className="text-default-400">{e.handle}</span>}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <Chip size="sm" variant="flat" color={e.kind === 'tournament' ? 'secondary' : 'primary'}>
                                                {e.kind === 'tournament' ? 'Tournament' : 'League'}
                                            </Chip>
                                        </td>
                                        <td className="px-3 py-2 text-default-500" title={e.owner_email || undefined}>
                                            {e.owner || <span className="text-default-300">—</span>}
                                        </td>
                                        <td className="px-3 py-2 tabular-nums">{e.hosts}</td>
                                        <td className="px-3 py-2 tabular-nums">{e.views}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">{e.last_seen}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <h2 className="mb-2 text-lg font-semibold">Copied the embed code</h2>
            {d.copied.length === 0 ? (
                <div className="rounded-xl border border-default-200 py-10 text-center text-sm text-default-500">
                    No organizer has copied an embed code yet.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-default-200">
                    <table className="w-full min-w-[560px] text-sm">
                        <thead>
                            <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                <th className="px-3 py-2">Organizer</th>
                                <th className="px-3 py-2">Email</th>
                                <th className="px-3 py-2">Copied</th>
                            </tr>
                        </thead>
                        <tbody>
                            {d.copied.map((c) => (
                                <tr key={c.user_uid} className="border-b border-default-100 last:border-0">
                                    <td className="px-3 py-2 font-medium">{c.name || <span className="text-default-300">—</span>}</td>
                                    <td className="px-3 py-2 text-default-500">{c.email}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-default-500">{c.copied_at}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    </>;
}
